// ADR 8.10.26h §7 — the playground's per-tab draft.
//
// `sessionStorage`, never `localStorage`: a scratch pad should not outlive
// its tab, and on a shared machine a script holding a webhook URL would
// otherwise wait for the next person. Wiped on every sign-out too
// (`auth/signOutWipe.ts`). Every access is in `try`/`catch`, because the
// storage can be absent or throw; the page then simply starts from the
// example. No React here, in `spreadPref.ts`'s shape.
//
// Narrowed on read: an unknown or unavailable kind, or a non-string script
// or sample, reads `null`. `params` go through R5's `narrowParamValues`, the
// rule a stored flow gets, so a bad entry is dropped and the rest kept.

import { narrowParamValues } from '../dsl/params'
import { NODE_KINDS, type NodeKind } from '../flowDefinition'
import type { ScratchDraft } from './scratch'

export const PLAYGROUND_DRAFT_KEY = 'lingara.builder.playground.v1'

function narrow(value: unknown): ScratchDraft | null {
  if (typeof value !== 'object' || value === null) return null
  const row = value as Record<string, unknown>
  if (!NODE_KINDS.includes(row.kind as NodeKind)) return null
  if (typeof row.script !== 'string' || typeof row.sample !== 'string') return null
  return { kind: row.kind as NodeKind, script: row.script, sample: row.sample, params: narrowParamValues(row.params) }
}

export function readPlaygroundDraft(): ScratchDraft | null {
  try {
    const raw = window.sessionStorage.getItem(PLAYGROUND_DRAFT_KEY)
    return raw === null ? null : narrow(JSON.parse(raw))
  } catch {
    return null
  }
}

export function writePlaygroundDraft(draft: ScratchDraft): void {
  try {
    window.sessionStorage.setItem(PLAYGROUND_DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // A refusing storage costs the draft, never the page.
  }
}

export function clearPlaygroundDraft(): void {
  try {
    window.sessionStorage.removeItem(PLAYGROUND_DRAFT_KEY)
  } catch {
    // As above.
  }
}
